/**
 * What the app *is*, written down once, small enough to hand to a language model.
 *
 * The assistant has to know this whole application — every screen, every button,
 * every rule the family agreed to — and it has to know it cheaply. Sending the
 * source, or the family's data, or a long manual would be slow, expensive and
 * mostly wasted: almost none of it answers "where do I press to add a photo?".
 *
 * So the app describes itself here, in the fewest words that still answer a real
 * question, and `digest.ts` turns this into a few hundred lines of plain text
 * that goes in front of every question. Everything in this file is deliberately
 * terse — it is read by a machine far more often than by a person.
 *
 * Three kinds of knowledge, and they answer three different questions:
 *
 *   SCREENS + CONTROLS  →  "where is it?"       (the assistant can then show them)
 *   RULES               →  "why did it do that?" (the family's own decisions)
 *   LIMITS              →  "is this broken?"     (deliberate, or not yet built)
 *
 * The last one matters most. A person who says "this is broken" is usually
 * looking at something the app does on purpose, and the honest answer — "that is
 * how it was built, and here is why" — is only possible if the app knows the
 * difference. When neither a rule nor a limit explains it, it really may be a
 * fault, and the assistant offers to send it to the administrators.
 *
 * Adding a screen or a button? Add it here, put the matching `data-assist` on
 * the element, and the assistant can find it, explain it and point at it. There
 * is nothing else to teach it.
 */
import type { Feature } from '../../app/experience';

/** A short line in both languages, for anything the assistant may say without a model. */
export interface Bilingual {
  en: string;
  he: string;
}

export interface AppScreen {
  id: string;
  /** The address, with `:id` where a person or story goes. */
  route: string;
  name: Bilingual;
  /** One line: what this screen is for. */
  what: string;
  /** Only in Advanced mode when set — see app/experience.ts. */
  feature?: Feature;
  /** Editors and administrators only. */
  staffOnly?: boolean;
  /**
   * The screen has pages of its own under its address — Admin controls is a menu
   * at `/manage` and a page per section at `/manage/access`, `/manage/people`, … —
   * and every one of them is still this screen (see `screenForPath`).
   */
  pages?: boolean;
}

export interface AppControl {
  /** Matches the `data-assist` attribute on the element. */
  id: string;
  /** Screen id, or 'anywhere' for the shell's own furniture. */
  on: string;
  name: Bilingual;
  /** Where a person's eye should go: said in the caption when the button is off screen. */
  where: Bilingual;
  /** What pressing it does. */
  does: string;
  feature?: Feature;
}

export interface AppRule {
  id: string;
  /**
   * The rule, as the family decided it. Bilingual because the built-in fallback
   * says it word for word — a model would translate, but the fallback has no
   * model to do it with, and a Hebrew-speaking grandparent is exactly who ends
   * up reading these.
   */
  says: Bilingual;
  /** The complaint it explains, so the assistant can recognise the question. */
  answers: string;
}

export interface AppLimit {
  id: string;
  /** What the app does not do. */
  says: Bilingual;
  /** 'design' — decided on purpose. 'prototype' — not built yet, and will be. */
  kind: 'design' | 'prototype';
}

export interface GuideStep {
  /** `data-assist` id to spotlight. Omit for a step that is only words. */
  anchor?: string;
  /** Go here first. */
  route?: string;
  say: Bilingual;
  /**
   * Only include this step when the feature is *off*.
   *
   * This is what lets one walkthrough answer "how do I see the timeline?"
   * correctly for everybody. In Simple mode the answer is four steps — menu,
   * settings, turn on Advanced, then press the timeline. In Advanced mode it is
   * one step, because the first three have already been done, and walking
   * somebody through switching on a setting that is already on is the fastest
   * way to lose their trust.
   */
  onlyIfOff?: Feature;
}

export interface HowTo {
  id: string;
  /** What someone might call this, in either language. Used to match without a model. */
  asked: string[];
  title: Bilingual;
  steps: GuideStep[];
  /** Offer this as a starting suggestion in the panel. */
  suggest?: boolean;
  feature?: Feature;
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

export const SCREENS: readonly AppScreen[] = [
  { id: 'tree', route: '/tree', name: { en: 'Family Tree', he: 'עץ המשפחה' }, what: 'The whole family as circles, five generations. The home screen. Tap a circle for a quick card; that card opens the full profile.' },
  { id: 'world', route: '/world', name: { en: 'World', he: 'העולם' }, what: 'A globe showing where everyone lives and who is visiting where.', feature: 'tab.world' },
  { id: 'timeline', route: '/timeline', name: { en: 'Timeline', he: 'ציר זמן' }, what: "The family's days back through the years: stories, births and milestones on one line. Tapping a day opens everything inside it.", feature: 'tab.timeline' },
  { id: 'calendar', route: '/calendar', name: { en: 'Calendar', he: 'לוח שנה' }, what: 'The days coming up: birthdays, anniversaries, yahrzeits and the chagim.', feature: 'tab.calendar' },
  { id: 'passedDown', route: '/passed-down', name: { en: 'Passed Down', he: 'מדור לדור' }, what: "Every recipe, newest first, searchable. Tapping one opens its constellation: the recipe in the middle, the family's pictures and recordings around it.", feature: 'tab.passedDown' },
  { id: 'record', route: '/record', name: { en: 'Record a Story', he: 'הקלטת סיפור' }, what: 'Record a story with the microphone. Saved to the device every second, so nothing is lost.' },
  { id: 'profile', route: '/people/:id', name: { en: "Someone's profile", he: 'פרופיל של מישהו' }, what: 'One person: their names, dates, where they live, their stories, and stories about them. Their pictures, stories and recipes are on their circle in the tree, not here.' },
  { id: 'editPerson', route: '/people/:id/edit', name: { en: 'Edit a person', he: 'עריכת אדם' }, what: "Change someone's names, dates and details. Only the circle's keeper, their helpers and administrators can save; everyone else's edit becomes a suggestion." },
  { id: 'addRelative', route: '/people/:id/add', name: { en: 'Add a relative', he: 'הוספת קרוב משפחה' }, what: 'One page: their name, who they are related to and how, then a few optional details. Writes the connection as a plain sentence as you choose it, warns about duplicates, and can email them an invitation.' },
  { id: 'manage', route: '/manage', name: { en: 'Admin controls', he: 'בקרות מנהל' }, what: 'Run the family: a menu of pages, listed under ADMIN CONTROLS.', staffOnly: true, pages: true },
];

/** Overlays that open on top of whatever screen you are on — never a screen of their own. */
export const PANELS: readonly { id: string; name: Bilingual; what: string; feature?: Feature }[] = [
  { id: 'settings', name: { en: 'Settings', he: 'הגדרות' }, what: 'Advanced mode, text size, gestures, tab descriptions, Shabbat mode, and the assistant. Language is not here — it is in the menu. There is no appearance setting: the app is dark.' },
  { id: 'profile', name: { en: 'My profile', he: 'הפרופיל שלי' }, what: 'Your own circle, your recordings and what you are helping with.' },
  { id: 'notifications', name: { en: 'Notifications', he: 'התראות' }, what: 'What the family has been doing, and what is coming up.', feature: 'notifications' },
  { id: 'add', name: { en: 'Add', he: 'הוספה' }, what: 'The sheet with five choices: a photo, a milestone, an everyday event, a recorded story, or a recipe.' },
  { id: 'recipe', name: { en: 'A recipe', he: 'מתכון' }, what: "One recipe to cook from: tick-off ingredients, numbered steps, notes, pictures." },
  { id: 'recipeEditor', name: { en: 'The recipe editor', he: 'עורך המתכונים' }, what: 'Makes a recipe: say it to the microphone (or type it) and it is written up with numbered lines; say "line 5 should be…" or tap a line to change it. A private draft until Publish.' },
];

// ---------------------------------------------------------------------------
// Controls — everything the assistant can point a finger at
// ---------------------------------------------------------------------------

const DOCK: Bilingual = { en: 'the row of pictures at the bottom', he: 'שורת התמונות בתחתית' };
const CORNER: Bilingual = { en: 'the top corner', he: 'בפינה העליונה' };
const TREE_STRIP: Bilingual = { en: 'the row of round buttons at the top', he: 'שורת הכפתורים העגולים למעלה' };
const MENU: Bilingual = { en: 'inside the menu', he: 'בתוך התפריט' };
const IN_SETTINGS: Bilingual = { en: 'in Settings', he: 'בהגדרות' };
const IN_ADD: Bilingual = { en: 'in the Add sheet', he: 'בחלון ההוספה' };
const IN_ADMIN: Bilingual = { en: 'in Admin controls', he: 'בבקרות המנהל' };

export const CONTROLS: readonly AppControl[] = [
  // ---- the shell: the same furniture on every screen ----
  { id: 'nav.assistant', on: 'anywhere', name: { en: 'Help', he: 'עזרה' }, where: CORNER, does: 'Opens this assistant. It is on every screen.' },
  { id: 'nav.menu', on: 'anywhere', name: { en: 'Menu', he: 'תפריט' }, where: CORNER, does: 'Opens the menu: your profile, settings, the other language, and adding a relative.' },
  { id: 'nav.notifications', on: 'anywhere', name: { en: 'Notifications', he: 'התראות' }, where: CORNER, does: 'Opens the bell.', feature: 'notifications' },
  { id: 'dock.tree', on: 'anywhere', name: { en: 'Family Tree', he: 'עץ המשפחה' }, where: DOCK, does: 'Goes to the family tree.', feature: 'tab.timeline' },
  { id: 'dock.world', on: 'anywhere', name: { en: 'World', he: 'העולם' }, where: DOCK, does: 'Goes to the globe.', feature: 'tab.world' },
  { id: 'dock.timeline', on: 'anywhere', name: { en: 'Timeline', he: 'ציר זמן' }, where: DOCK, does: "Goes to the family's line of days.", feature: 'tab.timeline' },
  { id: 'dock.calendar', on: 'anywhere', name: { en: 'Calendar', he: 'לוח שנה' }, where: DOCK, does: 'Goes to what is coming up.', feature: 'tab.calendar' },
  { id: 'dock.passed-down', on: 'anywhere', name: { en: 'Passed Down', he: 'מדור לדור' }, where: DOCK, does: 'Goes to Passed Down (the book with a heart).', feature: 'tab.passedDown' },
  { id: 'dock.add', on: 'anywhere', name: { en: 'Add', he: 'הוספה' }, where: { en: 'the round + at the end of the bottom row', he: 'העיגול + בסוף השורה התחתונה' }, does: 'Opens the Add sheet. It is always the last button in the row.' },

  // ---- the menu ----
  { id: 'menu.profile', on: 'anywhere', name: { en: 'My profile', he: 'הפרופיל שלי' }, where: MENU, does: 'Opens your own circle.' },
  { id: 'menu.settings', on: 'anywhere', name: { en: 'Settings', he: 'הגדרות' }, where: MENU, does: 'Opens Settings.' },
  { id: 'menu.language', on: 'anywhere', name: { en: 'The other language', he: 'השפה השנייה' }, where: MENU, does: 'Switches between English and Hebrew at once, keeping your place.' },
  { id: 'menu.addRelative', on: 'anywhere', name: { en: 'Add a relative', he: 'הוספת קרוב משפחה' }, where: MENU, does: 'The one way to put a new person in the tree.' },
  { id: 'menu.welcome', on: 'anywhere', name: { en: 'Welcome message', he: 'הודעת הפתיחה' }, where: MENU, does: 'Shows the welcome message again. It comes up by itself once, and again for everyone when an administrator changes it.' },
  { id: 'menu.reportIssue', on: 'anywhere', name: { en: 'Report a problem', he: 'דיווח על בעיה' }, where: MENU, does: 'Tell the admins and developers about a problem or an idea.' },
  { id: 'menu.adminMode', on: 'anywhere', name: { en: 'Admin mode', he: 'מצב מנהל' }, where: MENU, does: 'Turns administrator powers on for this device. Administrators only.' },
  { id: 'menu.admin', on: 'anywhere', name: { en: 'Admin controls', he: 'בקרות מנהל' }, where: MENU, does: 'Opens Admin controls (Admin mode on). A Family Editor sees it as Manage Family.' },

  // ---- the Add sheet ----
  { id: 'add.photo', on: 'add', name: { en: 'Add a photo', he: 'הוספת תמונה' }, where: IN_ADD, does: 'Share photographs or video clips with the family.' },
  { id: 'add.milestone', on: 'add', name: { en: 'Add a milestone', he: 'הוספת אירוע משמעותי' }, where: IN_ADD, does: 'A wedding, an engagement, a brit, a bar or bat mitzvah, a new baby, a graduation, a new home.' },
  { id: 'add.event', on: 'add', name: { en: 'Add an event', he: 'הוספת אירוע' }, where: IN_ADD, does: 'An everyday day worth keeping.' },
  { id: 'add.story', on: 'add', name: { en: 'Record a story', he: 'הקלטת סיפור' }, where: IN_ADD, does: 'Goes to the recording screen and starts a story.' },
  { id: 'add.recipe', on: 'add', name: { en: 'Write down a recipe', he: 'לכתוב מתכון' }, where: IN_ADD, does: "Opens the recipe editor. For somebody else's recipe, use the + on their circle, or change whose it is there." },

  // ---- Passed Down ----
  { id: 'passed.search', on: 'passedDown', name: { en: 'Search', he: 'חיפוש' }, where: { en: 'at the top of Passed Down', he: 'בראש «מדור לדור»' }, does: 'Finds a recipe by any word in it, ingredients and names included.', feature: 'tab.passedDown' },

  // ---- a recipe's pictures ----
  { id: 'recipe.addPicture', on: 'recipe', name: { en: 'Add a picture', he: 'הוספת תמונה' }, where: { en: 'at the end of the recipe, under Pictures', he: 'בסוף המתכון, תחת «תמונות»' }, does: 'Adds pictures of the dish (or paste one).' },

  // ---- the tree ----
  { id: 'tree.centerMe', on: 'tree', name: { en: 'Centre on me', he: 'למרכז עליי' }, where: TREE_STRIP, does: 'Brings your own circle to the middle.' },
  { id: 'tree.entireFamily', on: 'tree', name: { en: 'Entire family', he: 'כל המשפחה' }, where: TREE_STRIP, does: 'Fits everybody on one screen. It turns gold while only one branch is showing — then it is the way back.' },
  { id: 'tree.find', on: 'tree', name: { en: 'Find someone', he: 'חיפוש אדם' }, where: TREE_STRIP, does: 'Search by English name, Hebrew name, nickname or maiden name.' },
  { id: 'tree.arrange', on: 'tree', name: { en: 'Arrange by age', he: 'סידור לפי גיל' }, where: TREE_STRIP, does: 'Switches between generations and age order.' },
  { id: 'tree.legend', on: 'tree', name: { en: 'How to read the tree', he: 'איך לקרוא את העץ' }, where: TREE_STRIP, does: 'Explains the colours and the dots.', feature: 'tree.legend' },

  // ---- add a relative ----
  { id: 'addRelative.name', on: 'addRelative', name: { en: 'Their name', he: 'השם שלהם' }, where: { en: 'the first field on the page', he: 'השדה הראשון בעמוד' }, does: 'The first thing on the page, with the cursor already in it. Everything below it can be left alone.' },
  { id: 'addRelative.relatedTo', on: 'addRelative', name: { en: 'Related to', he: 'קשור/ה ל' }, where: { en: 'under the name', he: 'מתחת לשם' }, does: 'Who the new person hangs off. Change it to add a relative of somebody else — a grandfather is a parent of your father.' },
  { id: 'addRelative.relation', on: 'addRelative', name: { en: 'Parent, partner, child or sibling', he: 'הורה, בן/בת זוג, ילד/ה או אח/ות' }, where: { en: 'the row of four', he: 'שורת הארבעה' }, does: 'How the new person is related to whoever is in the Related to field. The sentence underneath says it back in words.' },
  { id: 'addRelative.email', on: 'addRelative', name: { en: 'Their email', he: 'המייל שלהם' }, where: { en: 'near the foot of the page', he: 'בתחתית העמוד' }, does: 'Optional. Fill it in and an invitation is emailed to them the moment you press Add; leave it blank and nothing is sent.' },

  // ---- settings ----
  { id: 'settings.advanced', on: 'settings', name: { en: 'Advanced mode', he: 'מצב מתקדם' }, where: { en: 'at the top of Settings', he: 'בראש ההגדרות' }, does: 'On by default. Turning it off hides the globe, the timeline, the calendar and the bell, leaving the tree.' },
  { id: 'settings.textSize', on: 'settings', name: { en: 'Text size', he: 'גודל טקסט' }, where: IN_SETTINGS, does: 'Makes every word in the app bigger.' },
  { id: 'settings.gestures', on: 'settings', name: { en: 'Tap to go back', he: 'הקשה לחזרה' }, where: IN_SETTINGS, does: 'Whether tapping the empty space around a day closes it.' },
  { id: 'settings.tabTips', on: 'settings', name: { en: 'Show the tab descriptions again', he: 'להציג שוב את ההסברים על הלשוניות' }, where: IN_SETTINGS, does: 'Brings back every tab’s description after its × was pressed.' },
  { id: 'settings.shabbat', on: 'settings', name: { en: 'Shabbat mode', he: 'מצב שבת' }, where: IN_SETTINGS, does: 'Keeps the bell quiet while it is Shabbat where you are.' },
  { id: 'settings.assistant', on: 'settings', name: { en: 'Assistant', he: 'העוזר' }, where: IN_SETTINGS, does: 'Whether the assistant listens to your voice and reads its answers out loud.' },

  // ---- admin ----
  // One per row of the Admin controls menu (features/admin/sections.ts — tests/adminSections.test.ts keeps the two
  // lists in step). The digest prints these compactly: a row's page is /manage/<the last part of its id>.
  { id: 'manage.section.review', on: 'manage', name: { en: 'Awaiting review', he: 'ממתין לבדיקה' }, where: IN_ADMIN, does: 'Approve or reject suggested changes.' },
  { id: 'manage.section.people', on: 'manage', name: { en: 'New people & circles', he: 'אנשים חדשים ועיגולים' }, where: IN_ADMIN, does: 'Approve a newcomer; remove or restore a circle.' },
  { id: 'manage.section.reports', on: 'manage', name: { en: 'From the family', he: 'מהמשפחה' }, where: IN_ADMIN, does: 'Reports sent through the assistant.' },
  { id: 'manage.section.duplicates', on: 'manage', name: { en: 'Possible duplicates', he: 'כפילויות אפשריות' }, where: IN_ADMIN, does: 'Merge two records of one person.' },
  { id: 'manage.section.jobs', on: 'manage', name: { en: 'Uploads & processing', he: 'העלאות ועיבוד' }, where: IN_ADMIN, does: 'Retry failed uploads.' },
  { id: 'manage.section.access', on: 'manage', name: { en: 'Invitations & codes', he: 'הזמנות וקודים' }, where: IN_ADMIN, does: 'Invite someone; family and device codes.' },
  { id: 'manage.section.admins', on: 'manage', name: { en: 'Administrators', he: 'מנהלים' }, where: IN_ADMIN, does: 'Suggest one, vote, step down.' },
  { id: 'manage.section.roles', on: 'manage', name: { en: 'Accounts & helpers', he: 'חשבונות ועוזרים' }, where: IN_ADMIN, does: 'Family Editors, wrong claims, helpers.' },
  { id: 'manage.section.moderation', on: 'manage', name: { en: 'Stories & recipes', he: 'סיפורים ומתכונים' }, where: IN_ADMIN, does: 'Lock, take down or put back one.' },
  { id: 'manage.section.locks', on: 'manage', name: { en: 'Locked records', he: 'רשומות נעולות' }, where: IN_ADMIN, does: 'Lock a circle or some details.' },
  { id: 'manage.section.decisions', on: 'manage', name: { en: 'Admin decisions', he: 'החלטות מנהלים' }, where: IN_ADMIN, does: 'What administrators did, and why.' },
  { id: 'manage.section.audit', on: 'manage', name: { en: 'History & restore', he: 'היסטוריה ושחזור' }, where: IN_ADMIN, does: 'Every change, and undoing one.' },
  { id: 'manage.section.welcome', on: 'manage', name: { en: 'Welcome message', he: 'הודעת הפתיחה' }, where: IN_ADMIN, does: 'Saving shows it to everyone again.' },
  { id: 'manage.section.tree', on: 'manage', name: { en: 'Tree display', he: 'תצוגת העץ' }, where: IN_ADMIN, does: 'How circles and badges grow.' },
  { id: 'manage.section.assistant', on: 'manage', name: { en: 'Assistant', he: 'העוזר' }, where: IN_ADMIN, does: 'The model, its key and the voice.' },
];

// ---------------------------------------------------------------------------
// Rules — the family's own decisions, which explain most "why did it do that?"
// ---------------------------------------------------------------------------

export const RULES: readonly AppRule[] = [
  {
    id: 'oneWayToAdd',
    says: { en: 'A person joins the tree from the menu only — Menu → Add a relative. There is deliberately no second way in.', he: 'מוסיפים אדם לעץ רק דרך התפריט — תפריט ← הוספת קרוב משפחה. אין דרך שנייה, וזה בכוונה.' },
    answers: "I can't find how to add someone / why isn't Add a person in the plus button?",
  },
  {
    id: 'recognition',
    says: { en: 'A newly added person is drawn dimmed until the family recognises them: two relatives saying "I know them", or one administrator approving at once.', he: 'אדם שהתווסף זה עתה מצויר עמום עד שהמשפחה מזהה אותו: שני קרובים שאומרים «אני מכיר אותו», או מנהל אחד שמאשר מיד.' },
    answers: 'Why is this person faded or greyed out?',
  },
  {
    id: 'ownership',
    says: { en: "Every circle has a keeper — the person themselves once they claim it, otherwise whoever added them. Only the keeper, the helpers they granted, and administrators can change a photo or details. Everyone else's edit is sent as a suggestion, with the reason attached.", he: 'לכל עיגול יש מי ששומר עליו — האדם עצמו אחרי שתבע אותו, ואחרת מי שהוסיף אותו. רק הוא, העוזרים שהרשה, ומנהלים יכולים לשנות תמונה או פרטים. עריכה של כל אחד אחר נשלחת כהצעה, עם הסיבה.' },
    answers: "Why can't I change this photo, why did my edit become a suggestion?",
  },
  {
    id: 'storyOwner',
    says: { en: 'A story belongs to whoever recorded it. They get one Edit button; everybody else gets the same form as Suggest a change, and the teller answers Yes, change it / No, leave it. Administrators edit directly, in the admin colour.', he: 'סיפור שייך למי שהקליט אותו. לו יש כפתור עריכה; לכל אחד אחר אותו טופס בתור הצעת שינוי, והמספר עונה «כן, לשנות» או «לא, להשאיר». מנהלים עורכים ישירות, בצבע המנהל.' },
    answers: "Why can't I edit this story?",
  },
  {
    id: 'recipesLive',
    says: { en: "A recipe is on the circle of whoever cooks it and whoever wrote it down (olive, with a cook's hat), and on Passed Down with all the others. Never on the timeline or calendar — a recipe is not a day that happened.", he: 'מתכון נמצא בשני מקומות: על העיגול של מי שמבשל אותו ושל מי שכתב אותו (מקישים על האדם בעץ — העיגול הירקרק עם כובע הטבח), ויחד עם כל השאר ב«מדור לדור», הספר עם הלב בתחתית. מתכונים בכוונה לא מופיעים בציר הזמן או בלוח השנה: מתכון אינו יום שקרה, אלא משהו שאדם מכין.' },
    // In both languages: the built-in matcher has only these words to recognise the question by.
    answers: 'Where are the recipes? איפה המתכונים?',
  },
  {
    id: 'recipeOwner',
    says: { en: 'A recipe is changed by whoever wrote it down, the person it belongs to (or their helper), and administrators. Drafts and unpublished changes stay private until Publish. One taken down is kept; an administrator can restore it.', he: 'מתכון יכולים לשנות מי שכתב אותו, מי שהמתכון שלו (או מי ששומר על העיגול שלו), ומנהלים. טיוטה נראית רק לכותב שלה עד שהיא מתפרסמת. גם שינויים במתכון שכבר פורסם נשארים פרטיים עד שלוחצים «פרסום השינויים». מתכון שהורד נשמר, ומנהל יכול להחזיר אותו.' },
    answers: "Why can't I edit this recipe? למה אי אפשר לערוך את המתכון?",
  },
  {
    id: 'recipePictures',
    says: { en: 'Anyone can add pictures to a recipe, story or day. The first is its main picture (on the Timeline too) until whoever may edit it opens another and presses Make this the main picture.', he: 'כל אחד במשפחה יכול להוסיף תמונות למתכון, לסיפור או ליום. התמונה הראשונה הופכת לתמונה הראשית — גם בציר הזמן; מי שרשאי לערוך יכול לפתוח כל תמונה אחרת וללחוץ «להפוך לתמונה הראשית».' },
    answers: 'How do I add a picture to a recipe, why is this the main picture, how do I change the picture of a story or a wedding on the timeline? איך מוסיפים תמונה למתכון? איך מחליפים את התמונה הראשית?',
  },
  {
    id: 'storyAudience',
    says: { en: 'A published story belongs to the whole family. There is no audience to choose and no way to narrow one. A draft stays private to the recorder until they publish it.', he: 'סיפור שפורסם שייך לכל המשפחה. אין קהל לבחור ואין דרך לצמצם אותו. טיוטה נשארת פרטית למקליט עד שהוא מפרסם.' },
    answers: 'How do I share a story with only some people?',
  },
  {
    id: 'conflict',
    says: { en: 'Two people editing different fields keeps both changes. Two people editing the same field shows both values, with who and when, and asks for an explicit choice — never a silent overwrite.', he: 'שני אנשים שעורכים שדות שונים — שני השינויים נשמרים. שניים שעורכים את אותו שדה — שני הערכים מוצגים, עם מי ומתי, ומבקשים לבחור. אף פעם לא דריסה שקטה.' },
    answers: 'Why am I being asked to choose between two values?',
  },
  {
    id: 'offline',
    says: { en: 'Edits made offline queue on the device and replay by themselves when the connection comes back. The bar at the top says how many are waiting.', he: 'עריכות שנעשו בלי חיבור ממתינות במכשיר ונשלחות מעצמן כשהחיבור חוזר. הפס למעלה אומר כמה ממתינות.' },
    answers: 'I made a change with no signal — is it lost?',
  },
  {
    id: 'modes',
    says: { en: 'Advanced mode is on by default: the globe, timeline, calendar, Passed Down, the bell and the tree legend. Simple mode (in Settings) keeps only the tree and the Add button; a link to an advanced screen then lands on the tree.', he: 'מצב מתקדם פועל אלא אם כיבו אותו: הגלובוס, ציר הזמן, לוח השנה, «מדור לדור», הפעמון ומקרא העץ. מצב פשוט (בהגדרות) משאיר רק את עץ המשפחה וכפתור ההוספה, וקישור למסך מתקדם מגיע לעץ במקום.' },
    answers: "Where did the globe, timeline, calendar or bell go? Why won't this link open?",
  },
  {
    id: 'removal',
    says: { en: "Taking a circle out of the tree is never one administrator's decision. It goes to agreement between administrators, with a reason, and archives rather than destroys.", he: 'הוצאת עיגול מהעץ אף פעם אינה החלטה של מנהל אחד. היא עוברת להסכמה בין המנהלים, עם סיבה, ומעבירה לארכיון במקום למחוק.' },
    answers: 'How do I delete somebody?',
  },
  {
    id: 'adminList',
    says: { en: 'Administrators are named by email. The list changes on a majority of those active lately, or by itself after 14 days if nobody objects — one objection closes that route. Nobody can appoint one alone.', he: 'מנהלים נקבעים לפי כתובת אימייל. הרשימה משתנה ברוב המנהלים שהיו פעילים לאחרונה, או מעצמה אחרי 14 יום אם אף אחד לא התנגד. התנגדות אחת סוגרת את הדרך השנייה.' },
    answers: 'How do I make someone an administrator?',
  },
  {
    id: 'adminColour',
    says: { en: 'Violet means administrator and nothing else. With Admin mode off there is no violet on screen at all.', he: 'סגול פירושו מנהל ותו לא. כשמצב מנהל כבוי אין בכלל סגול על המסך.' },
    answers: 'What does the purple mean?',
  },
  {
    id: 'futureMilestone',
    says: { en: 'A milestone whose day has not come stays off the timeline, because until then there is nothing inside it.', he: 'אירוע משמעותי שהיום שלו עוד לא הגיע לא מופיע בציר הזמן, כי עד אז אין בתוכו כלום.' },
    answers: "I added a wedding for next year and it isn't on the timeline.",
  },
  {
    id: 'yearsChosen',
    says: { en: 'Every year is chosen from a list rather than typed, so 9151 and next year are not answers anyone can give. A year already stored outside the list stays offered so an old record is never quietly changed.', he: 'כל שנה נבחרת מרשימה ולא מוקלדת, כך ש‑9151 או שנה הבאה אינן תשובות אפשריות. שנה ששמורה כבר מחוץ לרשימה ממשיכה להיות מוצעת, כדי שרשומה ישנה לא תשתנה בשקט.' },
    answers: "Why can't I type the year?",
  },
  {
    id: 'fuzzyDates',
    says: { en: 'Dates keep their uncertainty: exact, month, year, decade, a range, "about", "I was about 12", before or after an event, or unknown. Nothing is forced to be exact.', he: 'תאריכים שומרים על אי‑הוודאות שלהם: מדויק, חודש, שנה, עשור, טווח, «בערך», «הייתי בערך בן 12», לפני או אחרי אירוע, או לא ידוע. שום דבר לא נאלץ להיות מדויק.' },
    answers: "I don't know the exact date.",
  },
  {
    id: 'names',
    says: { en: 'Hebrew and English names are separate records and are never machine-translated. Nicknames and maiden names are searched too.', he: 'שמות בעברית ובאנגלית הם רשומות נפרדות ולעולם לא מתורגמים אוטומטית. גם כינויים ושמות נעורים נכללים בחיפוש.' },
    answers: 'Why is the Hebrew name blank or not translated?',
  },
  {
    id: 'photoThreeWays',
    says: { en: 'A picture added to a day is found inside that day and under the circle of whoever added it. It stays one entry, so a wedding with fifty photos is still one line.', he: 'תמונה שנוספה ליום נמצאת בתוך אותו יום ותחת העיגול של מי שהוסיף אותה. היא נשארת רשומה אחת, כך שחתונה עם חמישים תמונות היא עדיין שורה אחת.' },
    answers: 'Where did my photo go?',
  },
  {
    id: 'noMomentsTab',
    says: { en: 'There is no Moments tab, on purpose: a picture is looked for through the person it is of or the day it belongs to.', he: 'אין לשונית רגעים, בכוונה: מחפשים תמונה דרך האדם שבה או היום שאליו היא שייכת.' },
    answers: 'Where is the photos tab?',
  },
  {
    id: 'panelsStack',
    says: { en: 'Nothing costs you your place. A circle, a story, a face inside a story all open over what you were reading and stack, and closing any of them leaves you exactly where you were.', he: 'שום דבר לא עולה לך במקום שבו היית. עיגול, סיפור, פנים בתוך סיפור — כולם נפתחים מעל מה שקראת ומצטברים, וסגירה של כל אחד מהם מחזירה אותך בדיוק לאן שהיית.' },
    answers: 'Will I lose my place if I tap this?',
  },
  {
    id: 'shabbat',
    says: { en: 'With Shabbat mode on the bell keeps quiet — no count, no nudge — until Shabbat is over where you are. Nothing is lost; it waits.', he: 'כשמצב שבת פועל הפעמון שותק — בלי מספר ובלי תזכורת — עד צאת השבת במקום שבו אתם. שום דבר לא אובד; הכול ממתין.' },
    answers: 'Why are my notifications not showing?',
  },
  {
    id: 'recordingSaves',
    says: { en: 'A recording is written to the device every second and survives closing the tab. The status says what is true: Saved on this device → Uploading → Safely backed up.', he: 'הקלטה נכתבת למכשיר כל שנייה ושורדת סגירה של הלשונית. הסטטוס אומר את האמת: נשמר במכשיר ← מעלה ← מגובה בבטחה.' },
    answers: 'I closed the tab while recording — is it gone?',
  },
];

// ---------------------------------------------------------------------------
// Limits — what the app does not do, and whether that is on purpose
// ---------------------------------------------------------------------------

export const LIMITS: readonly AppLimit[] = [
  { id: 'noServer', kind: 'prototype', says: { en: 'There is no server and no account yet. The whole family, every recording and every change lives in this browser, on this device. Nothing reaches another person or another device.', he: 'עדיין אין שרת ואין חשבון. כל המשפחה, כל הקלטה וכל שינוי נמצאים בדפדפן הזה, במכשיר הזה. שום דבר לא מגיע לאדם אחר או למכשיר אחר.' } },
  { id: 'realtimeTabs', kind: 'prototype', says: { en: 'What looks like live updating is only between tabs on this one computer.', he: 'מה שנראה כמו עדכון חי הוא רק בין לשוניות במחשב הזה.' } },
  { id: 'passwords', kind: 'prototype', says: { en: 'Passwords are not checked. Any password works for a demo account.', he: 'סיסמאות אינן נבדקות. כל סיסמה עובדת לחשבון הדגמה.' } },
  { id: 'uploads', kind: 'prototype', says: { en: 'Uploading and backup are simulated. A story that says "Safely backed up" is not really anywhere else.', he: 'העלאה וגיבוי הם הדמיה. סיפור שכתוב עליו «מגובה בבטחה» אינו באמת בשום מקום אחר.' } },
  { id: 'transcripts', kind: 'prototype', says: { en: 'A newly recorded story is not transcribed. Only the stories that came with the demo have text.', he: 'סיפור שהוקלט עכשיו אינו מתומלל. רק לסיפורים שהגיעו עם ההדגמה יש טקסט.' } },
  { id: 'serverRules', kind: 'prototype', says: { en: 'Permission rules run in the browser only. In production they would also run on the server.', he: 'כללי ההרשאות רצים רק בדפדפן. בגרסה אמיתית הם ירוצו גם בשרת.' } },
  { id: 'noDelete', kind: 'design', says: { en: 'Nothing is ever deleted. A circle is archived by agreement between administrators; a picture taken down is kept so the decision can be undone.', he: 'שום דבר לא נמחק. עיגול עובר לארכיון בהסכמת המנהלים; תמונה שהוסרה נשמרת כדי שאפשר יהיה לבטל את ההחלטה.' } },
  { id: 'noAudience', kind: 'design', says: { en: 'A published story cannot be narrowed to some of the family.', he: 'אי אפשר לצמצם סיפור שפורסם לחלק מהמשפחה.' } },
  { id: 'noTypedYear', kind: 'design', says: { en: 'A year cannot be typed freehand.', he: 'אי אפשר להקליד שנה באופן חופשי.' } },
  { id: 'httpsToRecord', kind: 'design', says: { en: 'Recording needs a secure page. On a phone that means https — `npm run dev:https` — not a plain http address. The record screen says so when it cannot reach the microphone.', he: 'הקלטה דורשת עמוד מאובטח. בטלפון זה אומר https — `npm run dev:https` — ולא כתובת http רגילה. מסך ההקלטה אומר זאת כשאין גישה למיקרופון.' } },
  { id: 'noPersonInAdd', kind: 'design', says: { en: 'The plus button cannot add a person. That is the menu, on purpose.', he: 'כפתור הפלוס לא מוסיף אדם. זה בתפריט, בכוונה.' } },
  { id: 'recipeNeverInvents', kind: 'design', says: { en: 'Writing up a recipe adds nothing that was not said — no amounts, times or steps. A gap stays a gap, for a person to fill.', he: 'כשמתכון נכתב ממה שמישהו אמר, שום דבר לא מתווסף: לא כמויות, לא זמנים ולא שלבים שלא נאמרו. חסר במתכון משפחתי נשאר חסר, עד שאדם ישלים אותו.' } },
  { id: 'assistantCannotSave', kind: 'design', says: { en: 'The assistant can take you to a screen, point at the button and fill a form in, but it never presses Save. The last tap is always a person\u2019s.', he: 'העוזר יכול לקחת אתכם למסך, להצביע על הכפתור ולמלא טופס, אבל הוא לעולם לא לוחץ שמירה. הלחיצה האחרונה תמיד שלכם.' } },
];

// ---------------------------------------------------------------------------
// How-tos — the walks the assistant can give without a model at all
// ---------------------------------------------------------------------------

export const HOWTOS: readonly HowTo[] = [
  {
    id: 'addPhoto',
    asked: ['add a photo', 'add photos', 'upload a picture', 'share a picture', 'put a photo in', 'pictures', 'תמונה', 'להוסיף תמונה', 'להעלות תמונה'],
    title: { en: 'Add a photo', he: 'להוסיף תמונה' },
    suggest: true,
    steps: [
      { anchor: 'dock.add', say: { en: 'Press the + at the bottom.', he: 'לוחצים על ה־+ בתחתית.' } },
      { anchor: 'add.photo', say: { en: 'Choose “Add a photo”, then pick your pictures.', he: 'בוחרים «הוספת תמונה» ואז בוחרים את התמונות.' } },
    ],
  },
  {
    id: 'recordStory',
    asked: ['record a story', 'record', 'tell a story', 'microphone', 'voice', 'audio', 'להקליט', 'סיפור', 'הקלטה'],
    title: { en: 'Record a story', he: 'להקליט סיפור' },
    suggest: true,
    steps: [
      { anchor: 'dock.add', say: { en: 'Press the + at the bottom.', he: 'לוחצים על ה־+ בתחתית.' } },
      { anchor: 'add.story', say: { en: 'Choose “Record a story”.', he: 'בוחרים «הקלטת סיפור».' } },
      { route: '/record', say: { en: 'Press the big red button and talk. It saves every second.', he: 'לוחצים על הכפתור האדום הגדול ומדברים. זה נשמר כל שנייה.' } },
    ],
  },
  {
    id: 'addRecipe',
    /*
     * Phrases only, and only three a language.
     *
     * Never the bare word "recipe": "where are the recipes?" and "why can't I
     * edit this recipe?" both contain it, and neither is asking to write one.
     * And few of them, because every phrase here that shares that word hands a
     * little score to *any* question mentioning it — eight of them were enough,
     * between them, to walk "why can't I edit this recipe" to the Add sheet
     * without a single phrase actually matching.
     */
    asked: ['add a recipe', 'write down a recipe', 'new recipe', 'להוסיף מתכון', 'לכתוב מתכון', 'מתכון חדש'],
    title: { en: 'Write down a recipe', he: 'לכתוב מתכון' },
    suggest: true,
    steps: [
      { anchor: 'dock.add', say: { en: 'Press the + at the bottom.', he: 'לוחצים על ה־+ בתחתית.' } },
      { anchor: 'add.recipe', say: { en: 'Choose “Write down a recipe”. Then press the microphone and tell it the way you would across the kitchen table.', he: 'בוחרים «לכתוב מתכון». אחר כך לוחצים על המיקרופון ומספרים אותו כמו שמספרים ליד השולחן במטבח.' } },
    ],
  },
  {
    id: 'findRecipe',
    asked: ['find a recipe', 'all the recipes', 'search the recipes', 'למצוא מתכון', 'כל המתכונים', 'לחפש מתכון'],
    title: { en: 'Find a recipe', he: 'למצוא מתכון' },
    feature: 'tab.passedDown',
    steps: [
      { anchor: 'dock.passed-down', say: { en: 'Press the book with a heart at the bottom.', he: 'לוחצים על הספר עם הלב בתחתית.' } },
      { anchor: 'passed.search', say: { en: 'Type a word — the dish, something in it, or whose it is. Or tap a face to see only theirs.', he: 'מקלידים מילה — המנה, משהו שיש בה, או של מי היא. או מקישים על פנים כדי לראות רק את המתכונים שלהם.' } },
    ],
  },
  {
    id: 'addRecipePicture',
    asked: ['add a picture to a recipe', 'picture of the dish', 'main picture', 'להוסיף תמונה למתכון', 'תמונה של המנה', 'התמונה הראשית'],
    title: { en: 'Add a picture to a recipe', he: 'להוסיף תמונה למתכון' },
    steps: [
      { say: { en: 'Open the recipe.', he: 'פותחים את המתכון.' } },
      { anchor: 'recipe.addPicture', say: { en: 'At the end, under Pictures, press Add a picture — or paste one. The first picture becomes the main one; to choose another, open it and press Make this the main picture.', he: 'בסוף, תחת «תמונות», לוחצים «הוספת תמונה» — או מדביקים אחת. התמונה הראשונה הופכת לראשית; כדי לבחור אחרת, פותחים אותה ולוחצים «להפוך לתמונה הראשית».' } },
    ],
  },
  {
    id: 'addEvent',
    asked: ['add an event', 'new event', 'add a day', 'something happened', 'אירוע', 'להוסיף אירוע'],
    title: { en: 'Add an event', he: 'להוסיף אירוע' },
    suggest: true,
    steps: [
      { anchor: 'dock.add', say: { en: 'Press the + at the bottom.', he: 'לוחצים על ה־+ בתחתית.' } },
      { anchor: 'add.event', say: { en: 'Choose “Add an event” and give it a day.', he: 'בוחרים «הוספת אירוע» ונותנים לו תאריך.' } },
    ],
  },
  {
    id: 'addMilestone',
    asked: ['add a wedding', 'milestone', 'simcha', 'bar mitzvah', 'bat mitzvah', 'brit', 'engagement', 'graduation', 'new baby', 'חתונה', 'בר מצווה', 'בת מצווה', 'ברית', 'שמחה'],
    title: { en: 'Add a milestone', he: 'להוסיף אירוע משמעותי' },
    steps: [
      { anchor: 'dock.add', say: { en: 'Press the + at the bottom.', he: 'לוחצים על ה־+ בתחתית.' } },
      { anchor: 'add.milestone', say: { en: 'Choose “Add a milestone” and pick which kind.', he: 'בוחרים «הוספת אירוע משמעותי» ובוחרים איזה סוג.' } },
    ],
  },
  {
    id: 'addRelative',
    asked: ['add a person', 'add someone', 'add a relative', 'new person', 'add my brother', 'add my mother', 'grow the tree', 'להוסיף אדם', 'להוסיף קרוב', 'קרוב משפחה'],
    title: { en: 'Add someone to the tree', he: 'להוסיף מישהו לעץ' },
    suggest: true,
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.addRelative', say: { en: 'Choose “Add a relative”. This is the only way in — on purpose.', he: 'בוחרים «הוספת קרוב משפחה». זו הדרך היחידה, בכוונה.' } },
      { anchor: 'addRelative.name', say: { en: 'Type their name. Then say who they are related to and how, and press Add.', he: 'כותבים את השם. אחר כך בוחרים למי הם קשורים ואיך, ולוחצים «הוספה».' } },
    ],
  },
  {
    id: 'findPerson',
    /*
     * Not the bare "where is" / "איפה".
     *
     * "Where is" is how people ask for a *screen* far more often than for a
     * person — "where is the calendar", "where is the timeline", "איפה לוח
     * השנה" — and as a trigger it swallowed all of them and offered to search
     * the family for somebody called Calendar. A question word is not a
     * subject.
     */
    asked: ['find someone', 'find a person', 'search', 'look for a person', 'לחפש', 'למצוא', 'לחפש אדם', 'למצוא מישהו'],
    title: { en: 'Find someone in the tree', he: 'למצוא מישהו בעץ' },
    suggest: true,
    steps: [
      { route: '/tree', anchor: 'tree.find', say: { en: 'Press the magnifying glass and type any part of the name.', he: 'לוחצים על הזכוכית המגדלת ומקלידים חלק מהשם.' } },
    ],
  },
  {
    id: 'seeEveryone',
    asked: ['see everyone', 'whole family', 'zoom out', 'entire family', 'lost in the tree', 'כל המשפחה', 'לראות את כולם'],
    title: { en: 'See the whole family', he: 'לראות את כל המשפחה' },
    steps: [
      { route: '/tree', anchor: 'tree.entireFamily', say: { en: 'Press this to fit everybody on the screen.', he: 'לוחצים כאן כדי להתאים את כולם למסך.' } },
    ],
  },
  {
    id: 'changeLanguage',
    /*
     * Phrases, not the bare words "hebrew" and "english".
     *
     * Half of this app is in Hebrew, so half of the questions asked of it
     * mention Hebrew — "why is the Hebrew name blank", "does it read Hebrew
     * out", "the Hebrew looks wrong". Those two words on their own matched all
     * of them and walked everybody to the language switch, which answers none
     * of them. A word that appears in every question is not a trigger.
     */
    asked: [
      'change language', 'switch language', 'change the language', 'switch to hebrew', 'switch to english', 'change to hebrew', 'change to english', 'read it in hebrew', 'read it in english',
      'לשנות שפה', 'להחליף שפה', 'משנים שפה', 'מחליפים שפה', 'לעבור לעברית', 'לעבור לאנגלית',
    ],
    title: { en: 'Change the language', he: 'להחליף שפה' },
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.language', say: { en: 'Press the other language. You keep your place.', he: 'לוחצים על השפה השנייה. נשארים באותו מקום.' } },
    ],
  },
  {
    id: 'tabDescriptions',
    asked: ['tab descriptions', 'what is this tab for', 'bring back the descriptions', 'ההסברים על הלשוניות', 'בשביל מה הלשונית', 'להחזיר את ההסברים'],
    title: { en: 'See what each tab is for again', he: 'לראות שוב בשביל מה כל לשונית' },
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.settings', say: { en: 'Choose Settings.', he: 'בוחרים הגדרות.' } },
      { anchor: 'settings.tabTips', say: { en: 'Press “Show the tab descriptions again”. Each tab at the bottom will say what it is for until you close it with the ×.', he: 'לוחצים «להציג שוב את ההסברים על הלשוניות». כל לשונית בתחתית תסביר בשביל מה היא, עד שסוגרים אותה ב־×.' } },
    ],
  },
  {
    id: 'readWelcome',
    asked: ['welcome message', 'read the welcome again', 'the introduction', 'הודעת הפתיחה', 'לקרוא שוב את הפתיחה', 'דברי הפתיחה'],
    title: { en: 'Read the welcome message again', he: 'לקרוא שוב את הודעת הפתיחה' },
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.welcome', say: { en: 'Choose “Welcome message”. Administrators change what it says in Admin controls → Welcome message.', he: 'בוחרים «הודעת הפתיחה». מנהלים משנים את הנוסח שלה בבקרות המנהל ← הודעת הפתיחה.' } },
    ],
  },
  {
    id: 'biggerText',
    asked: ['bigger text', 'text size', 'font size', "can't read", 'too small', 'טקסט גדול', 'גודל טקסט', 'קטן מדי'],
    title: { en: 'Make the writing bigger', he: 'להגדיל את הכתב' },
    suggest: true,
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.settings', say: { en: 'Choose Settings.', he: 'בוחרים הגדרות.' } },
      { anchor: 'settings.textSize', say: { en: 'Pick a bigger size. Everything grows at once.', he: 'בוחרים גודל גדול יותר. הכול גדל מיד.' } },
    ],
  },
  {
    id: 'seeTimeline',
    asked: ['see the timeline', 'timeline', 'the family line', 'go to the timeline', 'all the stories', 'ציר זמן', 'לראות את ציר הזמן', 'הסיפורים'],
    title: { en: 'Go to the Timeline', he: 'לעבור לציר הזמן' },
    steps: [
      { anchor: 'nav.menu', onlyIfOff: 'tab.timeline', say: { en: 'That one is hidden right now. Open the menu.', he: 'זה מוסתר כרגע. פותחים את התפריט.' } },
      { anchor: 'menu.settings', onlyIfOff: 'tab.timeline', say: { en: 'Choose Settings.', he: 'בוחרים הגדרות.' } },
      { anchor: 'settings.advanced', onlyIfOff: 'tab.timeline', say: { en: 'Turn on Advanced mode.', he: 'מפעילים מצב מתקדם.' } },
      { anchor: 'dock.timeline', say: { en: 'Press the timeline at the bottom.', he: 'לוחצים על ציר הזמן בתחתית.' } },
    ],
  },
  {
    id: 'seeWorld',
    asked: ['see the globe', 'globe', 'the world', 'world map', 'where everyone lives', 'גלובוס', 'העולם', 'מפה', 'איפה כולם גרים'],
    title: { en: 'Go to the World globe', he: 'לעבור לגלובוס' },
    steps: [
      { anchor: 'nav.menu', onlyIfOff: 'tab.world', say: { en: 'That one is hidden right now. Open the menu.', he: 'זה מוסתר כרגע. פותחים את התפריט.' } },
      { anchor: 'menu.settings', onlyIfOff: 'tab.world', say: { en: 'Choose Settings.', he: 'בוחרים הגדרות.' } },
      { anchor: 'settings.advanced', onlyIfOff: 'tab.world', say: { en: 'Turn on Advanced mode.', he: 'מפעילים מצב מתקדם.' } },
      { anchor: 'dock.world', say: { en: 'Press the globe at the bottom.', he: 'לוחצים על הגלובוס בתחתית.' } },
    ],
  },
  {
    id: 'seeCalendar',
    asked: ['calendar', 'what is coming up', 'coming up', 'upcoming birthdays', 'whose birthday', 'לוח שנה', 'מה מתקרב', 'ימי הולדת'],
    title: { en: 'Go to the Calendar', he: 'לעבור ללוח השנה' },
    steps: [
      { anchor: 'nav.menu', onlyIfOff: 'tab.calendar', say: { en: 'That one is hidden right now. Open the menu.', he: 'זה מוסתר כרגע. פותחים את התפריט.' } },
      { anchor: 'menu.settings', onlyIfOff: 'tab.calendar', say: { en: 'Choose Settings.', he: 'בוחרים הגדרות.' } },
      { anchor: 'settings.advanced', onlyIfOff: 'tab.calendar', say: { en: 'Turn on Advanced mode.', he: 'מפעילים מצב מתקדם.' } },
      { anchor: 'dock.calendar', say: { en: 'Press the calendar at the bottom.', he: 'לוחצים על לוח השנה בתחתית.' } },
    ],
  },
  {
    id: 'advancedMode',
    asked: ['advanced mode', 'more features', 'missing tabs', 'where are the tabs', 'things are missing', 'simple mode', 'מצב מתקדם', 'חסרות לשוניות', 'מצב פשוט'],
    title: { en: 'Turn on Advanced mode', he: 'להפעיל מצב מתקדם' },
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.settings', say: { en: 'Choose Settings.', he: 'בוחרים הגדרות.' } },
      { anchor: 'settings.advanced', say: { en: 'Advanced mode is on by default — check the switch is on. It is what puts the globe, the timeline, the calendar and the bell there.', he: 'מצב מתקדם מופעל כברירת מחדל — בודקים שהמתג דלוק. הוא זה שמביא את הגלובוס, ציר הזמן, לוח השנה והפעמון.' } },
    ],
  },
  {
    /* Kept, with nothing to press: somebody who hunts for a light mode deserves
       a straight answer rather than a walk to a setting that is not there. */
    id: 'darkLight',
    asked: ['dark mode', 'light mode', 'too bright', 'too dark', 'theme', 'מצב כהה', 'מצב בהיר', 'בהיר מדי'],
    title: { en: 'Dark or light', he: 'כהה או בהיר' },
    steps: [
      { anchor: 'settings.textSize', say: { en: 'The app is dark everywhere and there is no light mode to switch to. If it is hard to read, Text size in Settings makes every word bigger.', he: 'האפליקציה כהה בכל מקום ואין מצב בהיר. אם קשה לקרוא, «גודל טקסט» בהגדרות מגדיל כל מילה.' } },
    ],
  },
  {
    id: 'myProfile',
    asked: ['my profile', 'my circle', 'my page', 'my recordings', 'הפרופיל שלי', 'העיגול שלי'],
    title: { en: 'Open my profile', he: 'לפתוח את הפרופיל שלי' },
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.profile', say: { en: 'Choose “My profile”.', he: 'בוחרים «הפרופיל שלי».' } },
    ],
  },
  {
    id: 'shabbatMode',
    asked: ['shabbat', 'shabbos', 'quiet', 'stop notifications', 'שבת', 'מצב שבת', 'שקט'],
    title: { en: 'Turn on Shabbat mode', he: 'להפעיל מצב שבת' },
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.settings', say: { en: 'Choose Settings.', he: 'בוחרים הגדרות.' } },
      { anchor: 'settings.shabbat', say: { en: 'Turn on Shabbat mode. The bell stays quiet until Shabbat is over.', he: 'מפעילים מצב שבת. הפעמון שותק עד צאת השבת.' } },
    ],
  },
  {
    id: 'adminControls',
    asked: ['admin', 'manage the family', 'administrator', 'review queue', 'מנהל', 'ניהול המשפחה'],
    title: { en: 'Open Admin controls', he: 'לפתוח בקרות מנהל' },
    steps: [
      { anchor: 'nav.menu', say: { en: 'Open the menu in the corner.', he: 'פותחים את התפריט בפינה.' } },
      { anchor: 'menu.adminMode', say: { en: 'Turn on Admin mode. It only shows if you are an administrator.', he: 'מפעילים מצב מנהל. זה מופיע רק למנהלים.' } },
      { anchor: 'menu.admin', say: { en: 'Choose “Admin controls”.', he: 'בוחרים «בקרות מנהל».' } },
    ],
  },
];

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

const CONTROLS_BY_ID = new Map(CONTROLS.map((c) => [c.id, c]));
const SCREENS_BY_ID = new Map(SCREENS.map((s) => [s.id, s]));

export function controlById(id: string): AppControl | undefined {
  return CONTROLS_BY_ID.get(id);
}

export function screenById(id: string): AppScreen | undefined {
  return SCREENS_BY_ID.get(id);
}

export function howToById(id: string): HowTo | undefined {
  return HOWTOS.find((h) => h.id === id);
}

/** The screen a path belongs to — `/people/abc` is the profile. */
export function screenForPath(path: string): AppScreen | undefined {
  const exact = SCREENS.find((s) => s.route === path);
  if (exact) return exact;
  // `/manage/access` is a page of Admin controls, not a screen nobody has heard of.
  const parent = SCREENS.find((s) => s.pages && path.startsWith(`${s.route}/`));
  if (parent) return parent;
  const parts = path.split('/').filter(Boolean);
  return SCREENS.find((s) => {
    const r = s.route.split('/').filter(Boolean);
    if (r.length !== parts.length) return false;
    return r.every((seg, i) => seg.startsWith(':') || seg === parts[i]);
  });
}
